import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { push } from 'connected-react-router';
import { Form, Formik } from 'formik';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';

import AcceptAgreementField from '../shared/AcceptAgreementField';
import Button from '../../../../shared/_TBComponents/Button';
import CommonFields from '../shared/CommonFields';
import GoogleAuthSection from '../shared/GoogleAuthSection';
import RegistrationFormWrapper from '../shared/RegistrationFormWrapper';
import { RegistrationSchemaGoogle, RegistrationSchemaIdnty } from '../../../../shared/utils/registrationSchemaHelper';
import { registerUser } from '../../ducks/registrationActions';
import { APP_STORE_URL, APP_URL_SCHEME, GOOGLE_PLAY_URL } from '../../constants';

const handleAlreadyHaveAccountClick = (dispatchFn) => {
  const { userAgent } = navigator;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  const isAndroid = /Android/.test(userAgent);

  if (isIOS) {
    const timeout = setTimeout(() => {
      window.location.href = APP_STORE_URL;
    }, 1500);

    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearTimeout(timeout);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    window.location.href = `${APP_URL_SCHEME}://`;
  } else if (isAndroid) {
    const fallback = encodeURIComponent(GOOGLE_PLAY_URL);
    window.location.href = `intent://#Intent;scheme=${APP_URL_SCHEME};package=com.teambuildr.reactnative.android.production;S.browser_fallback_url=${fallback};end`;
  } else {
    dispatchFn(push('/login'));
  }
};

const FormGroup = styled('div')`
  z-index: 1;

  label {
    width: 100%;
  }

  @media screen and (max-width: 425px) {
    margin-bottom: 10px;
  }
`;

const IdntyRegistrationForm = () => {
  const googleUser = useSelector((state) => state.registration.data.googleUser);
  const isRegisteringWithGoogle = useSelector(
    (state) => state.registration.data.isRegisteringWithGoogle,
  );
  const isSubmitting = useSelector((state) => state.registration.ui.isSubmitting);

  const theme = useTheme();
  const dispatch = useDispatch();
  const { search } = useLocation();
  const programId = new URLSearchParams(search).get('programId');

  const [googleResponse, setGoogleResponse] = useState(null);

  const handleRegistration = (values) => {
    dispatch(registerUser({
      acceptAgreement: values.acceptAgreement,
      accountCode: process.env.IDNTY_ACCT_CODE,
      authenticationMethod: isRegisteringWithGoogle ? 'GOOGLE' : 'MAGIC_LINK',
      client_id: process.env.CLIENT_ID,
      client_secret: process.env.CLIENT_SECRET,
      countryCode: values.countryCode,
      easyJoinId: process.env.IDNTY_EASY_JOIN_ID,
      emailAddress: values.emailAddress,
      firstName: values.firstName,
      gender: values.gender,
      lastName: values.lastName,
      mobileNumber: values.phone,
      ...(programId && { programId }),
      ...(isRegisteringWithGoogle && { googleToken: googleResponse?.tokenId }),
      registrationType: 'EXISTING_ORGANIZATION',
    }, true));
  };

  return (
    <Formik
      enableReinitialize
      validateOnMount
      initialValues={{
        acceptAgreement: false,
        countryCode: undefined,
        emailAddress: googleUser.email || '',
        firstName: googleUser.givenName || '',
        gender: undefined,
        lastName: googleUser.familyName || '',
        password: '',
        phone: undefined,
      }}
      onSubmit={handleRegistration}
      validationSchema={
        isRegisteringWithGoogle ? RegistrationSchemaGoogle : RegistrationSchemaIdnty
      }
    >
      {({
        errors,
        isValid,
        touched,
        handleSubmit,
        setFieldValue,
        values,
      }) => (
        <RegistrationFormWrapper
          subtitle='Enter the necessary details below to create your new account.'
          title='Registration'
          isIdnty
        >
          <Form
            id='registration'
            name='registration'
          >
            <GoogleAuthSection
              onGoogleConnect={setGoogleResponse}
            />
            <CommonFields
              errors={errors}
              setFieldValue={setFieldValue}
              showPasswordFields={false}
              touched={touched}
              values={values}
            />
            <AcceptAgreementField
              error={errors.acceptAgreement}
              setFieldValue={setFieldValue}
              touched={touched.acceptAgreement}
              values={{
                acceptAgreement: values.acceptAgreement,
              }}
            />
            <FormGroup>
              <Button
                bgColor='#00F5F5'
                disabled={!isValid || isSubmitting}
                fontSize='14px'
                fullWidth
                id='submit'
                large
                name='submit'
                noBorder
                onClick={handleSubmit}
                rounded
                txtColor='#000000'
                type='submit'
                upperCase
              />
            </FormGroup>
            <FormGroup>
              <Button
                fontSize='14px'
                fullWidth
                large
                noBorder
                name='Already have an account?'
                onClick={() => handleAlreadyHaveAccountClick(dispatch)}
                styledWrapperProps={{ marginTop: '15px' }}
                txtColor={theme.colors.lightText[1]}
                type='button'
                upperCase
              >
                Already have an account?
              </Button>
            </FormGroup>
          </Form>
        </RegistrationFormWrapper>
      )}
    </Formik>
  );
};

export default IdntyRegistrationForm;
